import React, { memo, useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { Textarea as TextareaHoi } from '@Sage/hoi-poi-ui';

import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';

const Textarea = ({
    autoFocus,
    fieldId,
    hidden,
    mandatory,
    maxLength,
    placeholderReadOnly,
    readOnly,
    label,
    hint,
    description,
    value,
    shouldRenderField,
    onChange,
    rows = 1,
    rowsMax,
    withoutResizer,
    error,
    labelMode,
}) => {
    const [text, setText] = useState(value);
    const prevValue = useRef(value);

    useEffect(() => {
        if (value !== text && prevValue.current !== value) setText(value);
        prevValue.current = value;
    }, [text, value]);

    // Real-time maxLength validation
    const errorLength = useMemo(() => {
        if (maxLength && maxLength > 0 && maxLength !== -1) {
            const strVal = text || '';
            if (strVal.length > maxLength) {
                return getLiteralWithParameters('error_text_too_long', [maxLength]);
            }
        }
        return '';
    }, [text, maxLength]);

    const onInnerChange = useCallback(
        (value, _, info) => {
            setText(value);
            if (info?.action === 'clear' && onChange) onChange(value);
        },
        [onChange],
    );

    const onBlur = useCallback(() => {
        onChange && onChange(text);
    }, [onChange, text]);

    if (hidden) return null;
    if (shouldRenderField && !shouldRenderField(fieldId)) return null;

    // Ensure minRows is at least 1
    const minRows = rows > 0 ? rows : 1;

    // External error takes priority to avoid duplication with FieldWrapper
    // Internal error (errorLength) only shows when no external error
    const finalError = error || errorLength;

    return (
        <TextareaHoi
            autoFocus={autoFocus}
            className="fm-field-textinput"
            label={label}
            labelMode={labelMode}
            hint={description}
            placeholder={hint || getLiteral('placeholder_text_field')}
            placeholderReadOnly={
                placeholderReadOnly || hint || getLiteral('placeholder_text_field')
            }
            isRequired={mandatory}
            isReadOnly={readOnly}
            onBlur={onBlur}
            onChange={onInnerChange}
            value={text}
            error={finalError}
            minRows={minRows}
            maxRows={rowsMax}
            isResizable={!withoutResizer}
            isFullWidth
        />
    );
};

export default memo(Textarea);
